import type {
  GeometryCollection,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
  Position,
} from 'geojson'

const WKB_POINT = 1
const WKB_LINESTRING = 2
const WKB_POLYGON = 3
const WKB_MULTIPOINT = 4
const WKB_MULTILINESTRING = 5
const WKB_MULTIPOLYGON = 6
const WKB_GEOMETRYCOLLECTION = 7
const EWKB_SRID_FLAG = 0x20_00_00_00

type EwkbGeometry =
  | GeometryCollection<EwkbGeometry>
  | LineString
  | MultiLineString
  | MultiPoint
  | MultiPolygon
  | Point
  | Polygon
type ParseResult = { geometry: EwkbGeometry; nextOffset: number }

// PostGIS X/Y geometries are always 2D; these type predicates narrow GeoJSON's `Position`
// (`number[]`) to the 2-tuples the parser builds, without an unsafe type assertion.
function is2DPosition(position: Position): position is [number, number] {
  return position.length === 2
}

function is2DPositionLine(line: Position[]): line is [number, number][] {
  return line.every((position) => is2DPosition(position))
}

function is2DPositionRings(rings: Position[][]): rings is [number, number][][] {
  return rings.every((ring) => is2DPositionLine(ring))
}

/**
 * Parse an EWKB (Extended Well-Known Binary) hex string from PostGIS into a GeoJSON geometry.
 * Supports Point, LineString, Polygon, MultiPoint, MultiLineString, MultiPolygon, and GeometryCollection.
 * Supports both WKB and EWKB formats (with or without SRID).
 */
export function parseEwkb(hex: string): EwkbGeometry {
  if (!hex || hex.length < 10) throw new Error('EWKB parser: input too short')

  const bytes = hexToBytes(hex)
  const view = new DataView(bytes.buffer)

  const result = parseGeometry(view, 0)
  return result.geometry
}

function parseGeometry(view: DataView, offset: number): ParseResult {
  if (offset + 5 > view.byteLength) {
    throw new Error('EWKB parser: unexpected end of input')
  }

  const byteOrder = view.getUint8(offset)
  const littleEndian = byteOrder === 1
  offset += 1

  let geomType = view.getUint32(offset, littleEndian)
  offset += 4

  if (geomType & EWKB_SRID_FLAG) {
    geomType = geomType & ~EWKB_SRID_FLAG
    offset += 4
  }

  switch (geomType) {
    case WKB_POINT: {
      const point = parsePoint(view, offset, littleEndian)
      return { geometry: point, nextOffset: offset + 16 }
    }
    case WKB_LINESTRING: {
      return parseLineString(view, offset, littleEndian)
    }
    case WKB_POLYGON: {
      return parsePolygon(view, offset, littleEndian)
    }
    case WKB_MULTIPOINT: {
      return parseMultiPoint(view, offset, littleEndian)
    }
    case WKB_MULTILINESTRING: {
      return parseMultiLineString(view, offset, littleEndian)
    }
    case WKB_MULTIPOLYGON: {
      return parseMultiPolygon(view, offset, littleEndian)
    }
    case WKB_GEOMETRYCOLLECTION: {
      return parseGeometryCollection(view, offset, littleEndian)
    }
    default: {
      throw new Error(`EWKB parser: unsupported geometry type ${geomType}`)
    }
  }
}

function parsePoint(view: DataView, offset: number, littleEndian: boolean): Point {
  const x = view.getFloat64(offset, littleEndian)
  const y = view.getFloat64(offset + 8, littleEndian)

  return {
    type: 'Point',
    coordinates: [x, y],
  }
}

function parseLineString(view: DataView, offset: number, littleEndian: boolean): ParseResult {
  const numPoints = view.getUint32(offset, littleEndian)
  offset += 4

  const coordinates: [number, number][] = []
  for (let i = 0; i < numPoints; i++) {
    const x = view.getFloat64(offset, littleEndian)
    const y = view.getFloat64(offset + 8, littleEndian)
    coordinates.push([x, y])
    offset += 16
  }

  return {
    geometry: {
      type: 'LineString',
      coordinates,
    },
    nextOffset: offset,
  }
}

function parsePolygon(view: DataView, offset: number, littleEndian: boolean): ParseResult {
  const numRings = view.getUint32(offset, littleEndian)
  offset += 4

  const coordinates: [number, number][][] = []
  for (let r = 0; r < numRings; r++) {
    const numPoints = view.getUint32(offset, littleEndian)
    offset += 4

    const ring: [number, number][] = []
    for (let i = 0; i < numPoints; i++) {
      const x = view.getFloat64(offset, littleEndian)
      const y = view.getFloat64(offset + 8, littleEndian)
      ring.push([x, y])
      offset += 16
    }
    coordinates.push(ring)
  }

  return {
    geometry: {
      type: 'Polygon',
      coordinates,
    },
    nextOffset: offset,
  }
}

function parseMultiPoint(view: DataView, offset: number, littleEndian: boolean): ParseResult {
  const numPoints = view.getUint32(offset, littleEndian)
  offset += 4

  const coordinates: [number, number][] = []
  for (let i = 0; i < numPoints; i++) {
    const result = parseGeometry(view, offset)
    if (result.geometry.type !== 'Point') {
      throw new Error('EWKB parser: invalid geometry in MultiPoint')
    }
    const position = result.geometry.coordinates
    if (!is2DPosition(position)) {
      throw new Error('EWKB parser: expected 2D position in MultiPoint')
    }
    coordinates.push(position)
    offset = result.nextOffset
  }

  return {
    geometry: {
      type: 'MultiPoint',
      coordinates,
    },
    nextOffset: offset,
  }
}

function parseMultiLineString(view: DataView, offset: number, littleEndian: boolean): ParseResult {
  const numLines = view.getUint32(offset, littleEndian)
  offset += 4

  const coordinates: [number, number][][] = []
  for (let i = 0; i < numLines; i++) {
    const result = parseGeometry(view, offset)
    if (result.geometry.type !== 'LineString') {
      throw new Error('EWKB parser: invalid geometry in MultiLineString')
    }
    const line = result.geometry.coordinates
    if (!is2DPositionLine(line)) {
      throw new Error('EWKB parser: expected 2D positions in MultiLineString')
    }
    coordinates.push(line)
    offset = result.nextOffset
  }

  return {
    geometry: {
      type: 'MultiLineString',
      coordinates,
    },
    nextOffset: offset,
  }
}

function parseMultiPolygon(view: DataView, offset: number, littleEndian: boolean): ParseResult {
  const numPolygons = view.getUint32(offset, littleEndian)
  offset += 4

  const coordinates: [number, number][][][] = []
  for (let i = 0; i < numPolygons; i++) {
    const result = parseGeometry(view, offset)
    if (result.geometry.type !== 'Polygon') {
      throw new Error('EWKB parser: invalid geometry in MultiPolygon')
    }
    const rings = result.geometry.coordinates
    if (!is2DPositionRings(rings)) {
      throw new Error('EWKB parser: expected 2D positions in MultiPolygon')
    }
    coordinates.push(rings)
    offset = result.nextOffset
  }

  return {
    geometry: {
      type: 'MultiPolygon',
      coordinates,
    },
    nextOffset: offset,
  }
}

function parseGeometryCollection(
  view: DataView,
  offset: number,
  littleEndian: boolean,
): ParseResult {
  const numGeometries = view.getUint32(offset, littleEndian)
  offset += 4

  const geometries: EwkbGeometry[] = []
  for (let i = 0; i < numGeometries; i++) {
    const result = parseGeometry(view, offset)
    geometries.push(result.geometry)
    offset = result.nextOffset
  }

  return {
    geometry: {
      type: 'GeometryCollection',
      geometries,
    },
    nextOffset: offset,
  }
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.slice(i, i + 2), 16)
  }
  return bytes
}
